import React, { useEffect, useRef, useState } from "react";
import background from "../../assets/mycenter.jpg";
import manifest from "../../manifest.json";
import { useRequestProcessor } from "../../requestProcessor";
import { useNavigate } from "react-router-dom";
import {
  handleLogin,
  handleVerifyOtp,
} from "../../QueryFunctions/queryfunction";
import { Clipboard } from "@capacitor/clipboard";
import MobileBlogs from "../Blogs/Blogs";
import SpinnerLoading from "../../Components/utils/SpinnerLoading";
import { getSignedInData, setLoginSuccess } from "../../AtomStore/ServiceAtoms";
import { useAtom } from "jotai";
import { Login_Form } from "../../AtomStore/store";
import { Capacitor } from "@capacitor/core";

const OtpInput = ({ length = 6, onComplete }) => {
  const [digits, setDigits] = useState(Array(length).fill(""));
  const [hasAutoFilled, setHasAutoFilled] = useState(false);
  const inputsRef = useRef([]);

  // ✅ Clear clipboard on mount to avoid filling old OTPs
  useEffect(() => {
    const clearClipboard = async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          await Clipboard.write({ string: "" });
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText("");
        }
        console.log("[OtpInput] Cleared clipboard on mount");
      } catch (err) {
        console.warn("Clipboard clear failed:", err);
      }
    };
    clearClipboard();
  }, []);

  // ✅ Notify parent when all digits are filled
  useEffect(() => {
    if (digits.every((d) => d !== "") && onComplete) {
      onComplete(digits.join(""));
    }
  }, [digits]);

  // ✅ Clipboard polling (auto-read only once)
  useEffect(() => {
    if (hasAutoFilled) return;

    const interval = setInterval(async () => {
      try {
        let value;
        if (Capacitor.isNativePlatform()) {
          const res = await Clipboard.read();
          value = res?.value ?? res;
        } else if (navigator.clipboard?.readText) {
          value = await navigator.clipboard.readText();
        }

        if (!value) return;
        value = String(value).trim().replace(/\D/g, "");

        if (value.length === length && digits.every((d) => d === "")) {
          const newDigits = value.split("");
          setDigits(newDigits);
          newDigits.forEach((d, i) => {
            if (inputsRef.current[i]) inputsRef.current[i].value = d;
          });
          inputsRef.current[length - 1]?.focus();
          setHasAutoFilled(true);
          console.log("[OtpInput] Auto-read clipboard applied:", value);

          clearInterval(interval); // Stop polling once filled
        }
      } catch (err) {
        console.error("Clipboard read error:", err);
      }
    }, 1500);

    return () => clearInterval(interval);
  }, [digits, hasAutoFilled]);

  // ✅ Handle manual typing
  const handleChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    if (!value) return;

    const newDigits = [...digits];
    if (value.length > 1) {
      value
        .split("")
        .slice(0, length)
        .forEach((d, i) => {
          newDigits[i] = d;
          if (inputsRef.current[i]) inputsRef.current[i].value = d;
        });
      setDigits(newDigits);
      inputsRef.current[length - 1]?.focus();
    } else {
      newDigits[index] = value[0];
      setDigits(newDigits);
      if (index < length - 1) inputsRef.current[index + 1]?.focus();
    }
  };

  // ✅ Handle backspace & navigation
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newDigits = [...digits];
      if (newDigits[index]) {
        newDigits[index] = "";
        setDigits(newDigits);
      } else if (index > 0) {
        newDigits[index - 1] = "";
        setDigits(newDigits);
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  // ✅ Handle paste (and clear clipboard right after)
  const handlePaste = async (e, index) => {
    e.preventDefault();
    const pasteData = (e.clipboardData?.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, length);
    if (!pasteData) return;

    const arr = pasteData.split("");
    const newDigits = Array(length).fill("");
    arr.forEach((d, i) => {
      newDigits[i] = d;
      if (inputsRef.current[i]) inputsRef.current[i].value = d;
    });
    setDigits(newDigits);
    inputsRef.current[length - 1]?.focus();
    setHasAutoFilled(true);
  };

  return (
    <div className="flex gap-2 justify-center mt-4">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          type="text"
          inputMode="numeric"
          value={digits[i]}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={(e) => handlePaste(e, i)}
          autoComplete="one-time-code"
          className="size-12 md:size-9 lg:size-12 border-3 text-[#dcdcdc] rounded-t-2xl rounded-bl-2xl text-center text-dark"
        />
      ))}
    </div>
  );
};

const LoginVerifyMobile = () => {
  const { useMutationWrapper } = useRequestProcessor();
  const [prevForm] = useAtom(Login_Form);
  const navigate = useNavigate();

  const [form, setForm] = useState({ error: "", OTP: "" });

  const verifyOtp = useMutationWrapper("verifyOtp", () =>
    handleVerifyOtp(form, setForm, navigate)
  );
  const Login = useMutationWrapper("Login", () =>
    handleLogin(getSignedInData())
  );

  const validate = () => {
    if (!form.OTP || form.OTP.length !== 6) {
      setForm({ ...form, error: "Please Enter OTP" });
      return false;
    }
    return true;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      <div className="hidden md:flex justify-center gap-3 mt-20 rounded-2xl overflow-visible">
        <div className="w-[60vw]">
          {/* <h1
            className="h-20 w-full text-white flex justify-center items-center text-3xl mb-2"
            style={{
              backgroundColor: "rgb(71 174 228 / var(--tw-bg-opacity, 1))",
            }}
          >
            Blogs
          </h1> */}
          <MobileBlogs />
        </div>
        <div className="w-[30vw] md:w-[40vw] lg:w-[30vw] flex-none [@media(max-width:1024px)]:w-[40vw]">
          <div className="sticky top-10">
            <div className="flex flex-col">
              {(verifyOtp.isLoading || Login.isLoading) && <SpinnerLoading />}
              <div
                className="h-60 bg-center bg-cover"
                style={{ backgroundImage: `url("${background}")` }}
              ></div>
              <div className="bg-white flex-grow rounded-t-[72px] mt-[-60px] px-4 flex flex-col justify-between mb-15">
                <div className="mb-20">
                  <i
                    onClick={() => setLoginSuccess(false)}
                    className="bi bi-arrow-left-short text-[#53bcda] text-[45px] relative top-4"
                  ></i>
                  <h3 className="text-dark font-extrabold text-lg mt-5">
                    Verification Code
                  </h3>
                  <p className="mt-2 font-medium leading-5">
                    We have sent the verification code to your{" "}
                    {prevForm.phone ? "mobile number" : "email address"}
                    <br></br>
                    <span className="text-red font-semibold">
                      OTP valid for 30 minutes only
                    </span>
                  </p>
                  {/* ✅ OTP Component */}
                  <OtpInput
                    length={6}
                    onComplete={(otp) => setForm({ ...form, OTP: otp })}
                    className="flex gap-2 justify-center mt-4"
                  />
                  {form.error && (
                    <p className="text-red leading-4 mt-2">{form.error}</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-center mb-2">
                    Didn't get the code?&nbsp;
                    <span
                      className="text-sm font-bold text-[#47aee4] cursor-pointer"
                      onClick={() => Login.mutate()}
                    >
                      Send Again
                    </span>
                  </h3>
                  <button
                    className="w-full rounded-full h-16 bg-[#47aee4] text-white text-lg font-bold"
                    onClick={() => validate() && verifyOtp.mutate()}
                  >
                    Submit
                  </button>
                  <h3 className="text-center mt-1 font-semibold">{`${manifest.applicationName} v${manifest.version}`}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Mobile View */}
      <div className="h-dvh overflow-y-auto flex flex-col md:hidden">
        {(verifyOtp.isLoading || Login.isLoading) && <SpinnerLoading />}
        <div
          className="h-60 bg-center bg-cover"
          style={{ backgroundImage: `url("${background}")` }}
        ></div>
        <div className="bg-white flex-grow rounded-t-[72px] mt-[-60px] px-4 flex flex-col justify-between mb-15">
          <div>
            <i
              onClick={() => setLoginSuccess(false)}
              className="bi bi-arrow-left-short text-[#53bcda] text-[45px] relative top-4 "
            ></i>
            <h3 className="text-dark text-center font-extrabold text-lg mt-5">
              Verification Code
            </h3>
            <p className="mt-2 font-medium leading-5 text-center">
              We have sent the verification code to your{" "}
              {prevForm.phone ? "mobile number" : "email address"}
              <br></br>{" "}
              <span className="text-red font-semibold">
                OTP valid for 30 minutes only
              </span>
            </p>
            {/* ✅ OTP Component */}
            <OtpInput
              length={6}
              onComplete={(otp) => setForm({ ...form, OTP: otp })}
            />
            {form.error && (
              <p className="text-red leading-4 mt-2">{form.error}</p>
            )}
          </div>
          <div className="mt-4">
            <h3 className="text-sm font-bold text-center mb-2">
              Didn't get the code?&nbsp;
              <span
                className="text-sm font-bold text-[#47aee4]"
                onClick={() => Login.mutate()}
              >
                Send Again
              </span>
            </h3>
            <button
              className="w-full rounded-full h-16 bg-[#47aee4] text-white text-lg font-bold"
              onClick={() => validate() && verifyOtp.mutate()}
            >
              Submit
            </button>
            <h3 className="text-center mt-1 font-semibold">{`${manifest.applicationName} v${manifest.version}`}</h3>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginVerifyMobile;
